/**
 * Responsibility: build the line grid that draws a water surface wherever the ground sits below a water level.
 *
 * ## Water goes through the structure pipeline, and that is the decision that made this cheap
 *
 * The obvious place for water is the terrain: clamp the drawn surface to the water level and colour it differently.
 * That would mean teaching the heightfield what water is, on the hottest path in the project, and teaching the palette
 * to colour something other than elevation. Both are invasive and neither is about elevation or colour.
 *
 * Emitting water as *structures* costs nothing new. The structure pipeline already writes arbitrary coloured line
 * segments into the tile's buffer and already supports a separate gradient per biome, so water needs no change to the
 * heightfield, the palette, `tile.js`, or anything in the render layer. Farmland's crop rows are the precedent: a long
 * run of ground lines emitted as one structure.
 *
 * ## The biome owns both the depth and the level, and the ground only decides the *extent*
 *
 * This has now been wrong in both directions, so it is worth stating what each version got right.
 *
 * **Version one asked "is the blended ground below the river's water level?"** The channel field is a global function of
 * position, so that painted the river network across every biome whose ground happened to be low: water stood on 24 of 40
 * farmland tiles and 30 of 41 city tiles at a river weight of *zero*, hanging up to 57 units above the fields.
 *
 * **Version two inverted it** — the biome supplied its own depth, that depth was scaled by biome weight, and the surface
 * was drawn at `ground + depth`. That confined the water correctly, but it made the surface a function of the terrain, and
 * **a lake surface is not supposed to be a function of the terrain.** At a biome weight below 1 the arithmetic came out as
 * `carvedGround * (1 - presence) + flatLevel * presence`, so a fraction of every bump in the ground was written straight
 * into the water. On glacial lakeland, whose ground relief is 120 units at a 900-unit wavelength, that read as visibly
 * lumpy water — which is exactly what was reported.
 *
 * **This version separates the two questions.** The biome supplies a **flat water level** and its **own depth**; the
 * blended ground decides only how far the water reaches:
 *
 * - **Height** comes from `waterLevelAt` alone, which is built from a long-wavelength field with no terrain detail in it.
 *   So the sheet is level, which is the whole point of a body of water.
 * - **Extent** is `min(ownDepth, waterLevel - blendedGround) * presence`. The first term confines water to the biome's own
 *   hollows, so it cannot spread across a low neighbour — the version-one bug. The second stops it sitting above ground
 *   the neighbour has lifted, and makes a lake taper in at a border as the blended ground rises through the level. The
 *   presence factor takes it smoothly to nothing where the biome fades out.
 * - Because depth only ever *gates and colours*, and never sets the height, terrain bumps can change where the waterline
 *   falls but can no longer tilt or ripple the surface itself.
 *
 * ## Why a world-aligned grid, clipped to the waterline
 *
 * A river crosses tiles, so the surface has to come off a lattice that knows nothing about tiles — anything
 * tile-relative shows a jog at every seam. Each cell edge is drawn only if at least one end is under water, and an edge
 * with one end dry is **shortened to the crossing point**, which is what gives a clean bank instead of a staircase.
 * That interpolation is the only thing standing between this and looking like a pixelated pond.
 *
 * ## Why the colour runs by depth
 *
 * A flat surface in one flat colour is the uniform expanse the steering file forbids. Interpolating the gradient by
 * depth instead of height gives the channel a bright core that fades to nothing at the waterline, so the ribbon reads
 * as a river rather than as a painted strip.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { emitTintedSegments } from './structureGeometry.js';
import { WATER } from '../config.js';

/** Floats a segment endpoint occupies in the flat array a water surface carries. */
const STRIDE = 8;

/**
 * How strongly water is present, from the owning biome's weight.
 *
 * Zero below `WATER.MIN_WEIGHT`, then rescaled across the range above it so the fade is smooth rather than a step. The
 * floor exists because a weight of a few percent still leaves a film a unit or two deep — measured at 25 segments a tile
 * of barely-visible water lying over a city — and there is nothing to gain from drawing that.
 *
 * **Exported because anything that reasons about where water *is* has to use this exact function.** Bridges did not, and
 * the result was a bridge standing on dry ground in a forest: the water needs a weight above the floor, the bridge search
 * only needed a weight above zero, and between the two there is a band where a channel exists in the biome's own terrain
 * but nothing is drawn.
 *
 * @param {number} weight
 * @returns {number} 0 to 1.
 */
export function presence(weight) {
  if (weight <= WATER.MIN_WEIGHT) return 0;
  return (weight - WATER.MIN_WEIGHT) / (1 - WATER.MIN_WEIGHT);
}

/**
 * Build a water surface collector.
 *
 * @param {object} options
 * @param {(x: number, z: number) => number} options.ownDepthAt - How deep this biome's water is at a coordinate, in
 *   world units, at full biome weight. **Signed**: negative on dry land, because the waterline is interpolated from it
 *   and a value clamped at zero puts the crossing at the dry end. This confines water to the biome's own hollows.
 * @param {(x: number, z: number) => number} options.waterLevelAt - Height the surface sits at, in world units.
 *   **Must contain no terrain detail** — it is what makes the sheet flat, so it should be built from the biome's
 *   longest-wavelength field and nothing else. Anything bumpy in here goes straight onto the water; see the note at the
 *   top of this file for the two ways this has been got wrong.
 * @param {(x: number, z: number) => boolean} options.couldHoldWater - Cheap rejection, before anything expensive. Water
 *   is confined to a channel covering a small share of the biome, and both the depth and the blended ground cost real
 *   samples — so this is what keeps the pass affordable.
 * @param {number} options.grid - Spacing of the surface lattice.
 * @param {number} options.maxDepth - Deepest this biome's water gets, used to normalise the depth the colour runs by.
 *   Passed in rather than read from a config block, because this module serves more than one water biome and they do not
 *   share a depth.
 * @returns {(region: object, context: object) => Array<object>}
 */
export function createWaterSurface({ ownDepthAt, waterLevelAt, couldHoldWater, grid, maxDepth }) {
  if (!(grid > 0)) throw new Error('createWaterSurface: grid must be positive');
  if (!(maxDepth > 0)) throw new Error('createWaterSurface: maxDepth must be positive');
  if (typeof waterLevelAt !== 'function') {
    throw new Error('createWaterSurface: waterLevelAt is required, or the surface will not be level');
  }

  /**
   * @param {{originX: number, originZ: number, size: number}} region
   * @param {object} context
   * @param {(x: number, z: number) => {elevation: number, slope: number}} context.sampleGroundDetail - The *drawn*
   *   blended surface. Blended matters: at a region border the neighbouring biome lifts the ground, and water has to
   *   stop there rather than sitting inside a hillside.
   * @returns {Array<object>} At most one structure, holding every segment for this tile.
   */
  return function collectStructures(region, { sampleWeight, sampleGroundDetail }) {
    /*
      A tile owns every grid cell whose **lower corner** falls inside it, and draws that cell whole — which means
      reaching one grid point past its own far edge.

      **Getting this wrong left a gap in every river.** Taking only the grid points inside the tile meant the cell
      straddling the boundary was drawn by neither side: with a 70-unit grid and 1,600-unit tiles the last point inside
      a tile sits at 1,540 and the next tile's first is at 1,610, so 70 units of river went missing at every seam. It
      reads as the water being cut into segments, which is exactly what was reported.
    */
    const first = { x: Math.ceil(region.originX / grid), z: Math.ceil(region.originZ / grid) };
    const farX = region.originX + region.size;
    const farZ = region.originZ + region.size;

    /*
      The last cell this tile owns. When the far edge lands exactly on a grid line that corner belongs to the *next*
      tile, or both would draw the same cell. Tile size is not a multiple of the grid today, so this branch is a guard
      against a future change to either number rather than a live case.
    */
    const lastOwned = {
      x: Math.floor(farX / grid) * grid === farX ? Math.floor(farX / grid) - 1 : Math.floor(farX / grid),
      z: Math.floor(farZ / grid) * grid === farZ ? Math.floor(farZ / grid) - 1 : Math.floor(farZ / grid),
    };

    // One point past the last owned corner, so that corner's cell can be drawn.
    const columns = lastOwned.x - first.x + 2;
    const rows = lastOwned.z - first.z + 2;
    if (columns <= 1 || rows <= 1) return [];

    /**
     * Signed depth of the drawn water. **Decides where the water is, and never how high it is.**
     *
     * Three factors, each answering a different question, and all three are load-bearing:
     *
     * - `ownDepthAt` confines water to this biome's own hollows. Without it the channel or basin field, which are global
     *   functions of position, draw water across every neighbour whose land happens to sit low — measured at water
     *   standing on 30 of 41 city tiles at a river weight of zero.
     * - `waterLevelAt - blendedGround` stops water sitting above ground a neighbour has lifted, and is what makes a lake
     *   taper in at a border as the blended ground rises through the level.
     * - `presence` takes it smoothly to nothing where the biome fades out, rather than cutting it off at a contour.
     *
     * Kept signed and unclamped: the waterline is interpolated from this along a cell edge, and a value clamped at zero
     * puts the crossing at the dry end so nothing is ever clipped.
     */
    const depthAt = (x, z) => {
      const room = waterLevelAt(x, z) - sampleGroundDetail(x, z).elevation;
      return Math.min(ownDepthAt(x, z), room) * presence(sampleWeight(x, z));
    };

    /*
      Depth and surface height at every grid point, laid out row-major, with a flag for what has been worked out yet.
      Flat arrays rather than objects: this is a few hundred entries per tile and the allocation showed up.
    */
    const depth = new Float32Array(columns * rows);
    const level = new Float32Array(columns * rows);
    const known = new Uint8Array(columns * rows);
    let wet = 0;

    for (let ix = 0; ix < columns; ix += 1) {
      const x = (first.x + ix) * grid;

      for (let iz = 0; iz < rows; iz += 1) {
        const z = (first.z + iz) * grid;

        /*
          Cheap first. Most of a tile is nowhere near a channel, and this costs one unblended noise sample against the
          biome weight and blended terrain samples it saves. The grid is coarse enough that a wet point can have a
          neighbour well outside the valley, so anything skipped here may still be needed — `resolve` below fills those
          in on demand.
        */
        if (!couldHoldWater(x, z)) continue;

        const index = ix * rows + iz;
        depth[index] = depthAt(x, z);
        known[index] = 1;

        if (depth[index] > 0) {
          /*
            The level, and nothing derived from the ground. **This one line is the difference between a lake and a
            lumpy sheet** — the previous version added the depth to the blended ground here, which wrote a fraction of
            every terrain bump into the surface.
          */
          level[index] = waterLevelAt(x, z);
          wet += 1;
        }
      }
    }

    if (wet === 0) return [];

    /**
     * Depth at a grid point, worked out now if the cheap pass skipped it.
     *
     * Only ever called for the dry end of an edge whose other end is under water, so this pays for the bank and
     * nothing else.
     */
    const resolve = (index, gx, gz) => {
      if (!known[index]) {
        depth[index] = depthAt(gx * grid, gz * grid);
        known[index] = 1;
      }
      return depth[index];
    };

    const segments = [];

    /** Store one segment as eight floats: two positions, each with the depth fraction that colours it. */
    const push = (x1, y1, z1, d1, x2, y2, z2, d2) => {
      segments.push(x1, y1, z1, Math.min(1, d1 / maxDepth), x2, y2, z2, Math.min(1, d2 / maxDepth));
    };

    /**
     * Add the segment for one cell edge, clipped to the waterline.
     *
     * @param {number} a - Index of the near end.
     * @param {number} b - Index of the far end.
     * @param {number} gx - Grid coordinate of the near end.
     * @param {number} gz
     * @param {number} stepX - Direction to the far end, in grid steps.
     * @param {number} stepZ
     */
    const addEdge = (a, b, gx, gz, stepX, stepZ) => {
      /*
        Both ends dry in the cheap pass means dry, full stop. Water is strictly inside the valley, so an edge that the
        cheap pass rejected at both ends cannot cross a waterline.
      */
      const rawA = known[a] ? depth[a] : 0;
      const rawB = known[b] ? depth[b] : 0;
      if (rawA <= 0 && rawB <= 0) return;

      const ax = gx * grid;
      const az = gz * grid;
      const bx = (gx + stepX) * grid;
      const bz = (gz + stepZ) * grid;

      // One end is under water, so the other is worth working out properly even if the cheap pass skipped it.
      const depthA = rawA > 0 ? rawA : resolve(a, gx, gz);
      const depthB = rawB > 0 ? rawB : resolve(b, gx + stepX, gz + stepZ);

      if (depthA > 0 && depthB > 0) {
        push(ax, level[a], az, depthA, bx, level[b], bz, depthB);
        return;
      }

      const wetFirst = depthA > 0;
      const fromX = wetFirst ? ax : bx;
      const fromZ = wetFirst ? az : bz;
      const toX = wetFirst ? bx : ax;
      const toZ = wetFirst ? bz : az;
      const wetDepth = wetFirst ? depthA : depthB;
      const dryDepth = wetFirst ? depthB : depthA;
      const surface = wetFirst ? level[a] : level[b];

      /*
        Cut the segment back to where the depth reaches zero, which is what turns a staircase of grid cells into a bank
        you would believe. Interpolated on the two ends' depths, which is why the sign of a dry point has to be kept:
        storing dry as zero put the crossing at the dry end and nothing was ever clipped.
      */
      const t = wetDepth / (wetDepth - dryDepth);
      const endX = fromX + (toX - fromX) * t;
      const endZ = fromZ + (toZ - fromZ) * t;

      /*
        **The one endpoint still taken from the ground rather than the level**, and deliberately so. Linear interpolation
        puts the crossing slightly off, so the level and the ground do not agree there exactly — and of the two errors,
        a shoreline floating just above the terrain or cut just below it is worse than the last fraction of a segment
        not being perfectly level. Below the terrain is the bad case in particular: the terrain casts a depth mask and
        the water does not, so a submerged edge is occluded and the shore appears to stop short.

        The cost is confined to the final partial segment of each edge, where the depth is near zero and the colour has
        already faded to nearly the background.
      */
      push(fromX, surface, fromZ, wetDepth, endX, sampleGroundDetail(endX, endZ).elevation, endZ, 0);
    };

    /*
      **Each owned corner emits exactly its two forward edges, and the extra row and column emit nothing.**

      They exist only to give the owned corners something to draw *to*. Letting them originate edges of their own drew
      the seam line twice — once by this tile's spare column and once by the neighbour whose first column it is. The
      first attempt at fixing the gap traded it for 53 duplicated segments per sixteen tiles, which a
      tiling-equals-the-whole test caught.
    */
    for (let ix = 0; ix < columns - 1; ix += 1) {
      for (let iz = 0; iz < rows - 1; iz += 1) {
        const index = ix * rows + iz;

        addEdge(index, (ix + 1) * rows + iz, first.x + ix, first.z + iz, 1, 0);
        addEdge(index, ix * rows + iz + 1, first.x + ix, first.z + iz, 0, 1);
      }
    }

    if (segments.length === 0) return [];

    return [
      {
        kind: 'waterSurface',
        /** Flat float list, eight per segment. One structure per tile rather than one per segment. */
        data: segments,
        count: segments.length / STRIDE,
      },
    ];
  };
}

/** Floats per segment in a water surface's `data`. Exported so the emitter and its counter cannot disagree. */
export { STRIDE as WATER_FLOATS_PER_SEGMENT };

/**
 * Offsets probed around an object's centre, at its own radius.
 *
 * **Eight, not four.** With only the axes, the worst-covered direction is the diagonal, so a shoreline running at 45
 * degrees could pass between the probes and a footprint would be reported dry with a corner of it in the water. The
 * diagonals are unit-normalised so all eight sit on the same circle rather than the corners of a square.
 */
const DIAGONAL = Math.SQRT1_2;

const SHORE_PROBES = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
  [DIAGONAL, DIAGONAL],
  [DIAGONAL, -DIAGONAL],
  [-DIAGONAL, DIAGONAL],
  [-DIAGONAL, -DIAGONAL],
];

/**
 * Build the test that says whether something would be standing in water.
 *
 * Published by a water-bearing biome as its `createReservation`, so **every** other biome's structures keep out of it
 * for free — trees, towers, rock columns, farm buildings and dishes all already consult the cross-biome reservation.
 * Nothing had to be added to any of them.
 *
 * ## Why nine probes rather than one
 *
 * A tree is not a point. Testing only its centre lets a crown hang half over the water, which is what was reported first.
 * The centre plus eight points around it at the object's own radius keeps the whole footprint dry, and needs no fudge
 * factor converting a radius into a depth.
 *
 * It went from five to nine because four axis-aligned probes leave the diagonals as the worst-covered directions, so a
 * shoreline at 45 degrees could slip between them. The extra four cost nothing in the common case: the loop stops at the
 * first wet probe, and the cheap gate above rejects everything not near water in a single noise sample.
 *
 * ## Why the gate is looser than the channel
 *
 * The cheap rejection has to be generous, not tight. Water ends well inside the valley, so an object *outside* the
 * channel can still be wide enough to reach it — a tight gate would reject exactly the cases this exists to catch.
 *
 * @param {object} options
 * @param {(x: number, z: number) => number} options.ownDepthAt - Signed depth, as for `createWaterSurface`.
 * @param {(x: number, z: number) => boolean} options.nearWater - Cheap rejection, deliberately generous.
 * @returns {(x: number, z: number, radius: number, context: object) => boolean}
 */
export function createWaterClearance({ ownDepthAt, nearWater }) {
  function standingInWater(x, z, radius, { sampleWeight }) {
    // Cheap first. Most of the world is nowhere near water and this costs one unblended noise sample.
    if (!nearWater(x, z)) return false;

    const reach = radius + WATER.SHORE_CLEARANCE;

    /*
      Geometry decides whether the footprint touches water; the biome's weight then decides whether that water is
      actually here. Taking the weight first would cost a biome-field sample on every candidate near a channel, and
      taking the depth first rejects most of them for less.
    */
    let touches = ownDepthAt(x, z) > 0;

    for (let i = 0; !touches && i < SHORE_PROBES.length; i += 1) {
      const [dx, dz] = SHORE_PROBES[i];
      touches = ownDepthAt(x + dx * reach, z + dz * reach) > 0;
    }

    if (!touches) return false;

    return presence(sampleWeight(x, z)) > 0;
  }

  /*
    **This claim is about the ground itself, not about something standing on it**, and the difference decides who
    yields when two biomes want the same spot.

    Almost every reservation in the world says "my buildings are here". Those can be worked around by agreement —
    if two biomes both consult each other, whichever one is told to keep clear does so and neither ends up inside
    the other. Water cannot be worked around. There is nothing on the other side of the negotiation, because the
    lake is simply there.

    So a biome may be allowed to outrank other biomes' *structures* while still being refused by water, and this
    flag is what lets `tileContent` tell the two apart. The fairground is the one that needs it: it is a single
    site that is either complete or absent, and it was losing eight parks in a 128,000-unit square to industrial
    estates that reserve a block wherever they have any weight at all and would have built nothing there.
  */
  standingInWater.claimsGround = true;

  return standingInWater;
}

/**
 * Emit a water surface.
 *
 * Kept next to the collector rather than in `structureGeometry.js` so the flat-array layout and the only thing that
 * reads it live in one file. Nothing here is renderer-specific: it writes coloured segments through the same writer
 * every other structure uses.
 *
 * @param {object} writer - From `createGeometryWriter`.
 * @param {{data: Array<number>, count: number}} water
 * @param {{low: object, high: object}} gradient
 * @returns {number} Segments written.
 */
export function emitWaterSurface(writer, water, gradient) {
  /*
    Colour by depth, not by height. The deepest line of the channel comes out brightest and the waterline fades to
    nearly the background, which is what stops a flat surface reading as a flat surface.

    The loop itself is `emitTintedSegments`, which is the generic version of exactly this: a flat float list where
    every vertex carries its own gradient fraction. The vineyard's contour lines needed the same thing to tell a
    retaining wall from a vine row, and one writing loop with two callers beats two loops.
  */
  return emitTintedSegments(writer, water.data, water.count, gradient);
}

